import React, { useState, useRef, useEffect } from "react";
import MetaTags from "react-meta-tags";
import { Row, Col, CardBody, Card, Alert, Container, Input, Label, Form, FormFeedback } from "reactstrap";
import { Link, useHistory } from "react-router-dom";
import ApiClient from "../../FindagramAdmin/BaseUrl/ApiClient";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useSelector, useDispatch } from "react-redux";
import { apiError } from "../../store/actions";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import bitelogo from "../../assets/images/bitelogo.png";

const ResetPassword = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [tokenInputs, setTokenInputs] = useState(["", "", "", "", "", ""]);
  const isMounted = useRef(true);

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      verificationCode: "",
      password: "",
      confirmPassword: "",
    },
    validationSchema: Yup.object({
      verificationCode: Yup.string()
        .matches(/^\d{6}$/, "Verification code must be 6 digits")
        .required("Please enter the verification code"),
      password: Yup.string()
        .min(8, "Password must be at least 8 characters")
        .required("Please enter your password"),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref("password"), null], "Passwords must match")
        .required("Please confirm your password"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const response = await ApiClient().put("/auth/admin/resetPassword", values);


        const { responseData } = response.data;
        //   console.log("responseData:", responseData);

        const { accessToken, refreshToken, user } = responseData;
        const { businessProfile } = responseData;

        const message = response?.data?.responseMessage?.[0] || " ";

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        localStorage.setItem("authUser", JSON.stringify(user));

        toast.success(message);

        setTimeout(() => {
          history.push("/login");
        }, 1500);
      } catch (error) {
        (error?.response?.data?.responseMessage || ["Try again pls"]).forEach(
          (msg) => {
            toast.error(msg);
          }
        );
      } finally {
        if (isMounted.current) {
          setLoading(false);
        }
      }
    },
  });

  const handleTokenChange = (index, value) => {
    if (/^\d?$/.test(value)) {
      const newTokenInputs = [...tokenInputs];
      newTokenInputs[index] = value;
      setTokenInputs(newTokenInputs);
      validation.setFieldValue("verificationCode", newTokenInputs.join(""));

      // Auto-focus next input
      if (value && index < 5) {
        document.getElementById(`token-${index + 1}`).focus();
      }
    }
  };

  const handleTokenKeyDown = (index, e) => {
    if (e.key === "Backspace" && !tokenInputs[index] && index > 0) {
      document.getElementById(`token-${index - 1}`).focus();
    }
  };

  const { registrationError } = useSelector((state) => ({
    registrationError: state.Account.registrationError,
  }));

  useEffect(() => {
    dispatch(apiError(""));
    return () => {
      isMounted.current = false;
    };
  }, [dispatch]);

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content">
          <MetaTags>
            <title>Reset Password | BiteSaver</title>
          </MetaTags>
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center text-white-50">
                  <Link to="/" className="d-inline-block auth-logo">
                    <   img src={bitelogo} alt="BiteSaver Logo" height="150" />
                  </Link>
                </div>
              </Col>
            </Row>

            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">Reset Password</h5>
                      <p className="text-muted">Enter the verification code and your new password.</p>
                    </div>
                    <div className="p-2 mt-4">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                          return false;
                        }}
                        className="needs-validation"
                        action="#"
                      >
                        {registrationError && registrationError ? (
                          <Alert color="danger">
                            <div>{registrationError}</div>
                          </Alert>
                        ) : null}

                        <div className="mb-3">
                          <Label htmlFor="verificationCode" className="form-label">
                            Verification Code <span className="text-danger">*</span>
                          </Label>
                          <div className="d-flex gap-2 justify-content-center">
                            {tokenInputs.map((char, i) => (
                              <Input
                                key={i}
                                id={`token-${i}`}
                                type="text"
                                maxLength={1}
                                className="form-control text-center"
                                style={{ width: "40px" }}
                                value={char}
                                onChange={(e) => handleTokenChange(i, e.target.value)}
                                onKeyDown={(e) => handleTokenKeyDown(i, e)}
                                invalid={
                                  validation.touched.verificationCode &&
                                  validation.errors.verificationCode
                                }
                              />
                            ))}
                          </div>
                          {validation.touched.verificationCode && validation.errors.verificationCode ? (
                            <FormFeedback type="invalid" className="d-block text-center">
                              {validation.errors.verificationCode}
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3 ">
                          <Label htmlFor="password" className="form-label">
                            Password <span className="text-danger">*</span>
                          </Label>
                          <div style={{ position: "relative" }}>
                            <Input
                              id="password"
                              name="password"
                              className="form-control"
                              placeholder="Enter password"
                              type={showPassword ? "text" : "password"}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.password || ""}
                              invalid={validation.touched.password && validation.errors.password}
                            />
                            <button
                              type="button"
                              className="position-absolute top-50 translate-middle-y pe-3 border-0 bg-transparent"
                              onClick={togglePasswordVisibility}
                              style={{
                                right: validation.touched.password && validation.errors.password ? "20px" : "0",
                                cursor: "pointer",
                              }}
                            >
                              <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                            </button>
                          </div>
                          {validation.touched.password && validation.errors.password ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.password}</div>
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mb-3">
                          <Label htmlFor="confirmPassword" className="form-label">
                            Confirm Password <span className="text-danger">*</span>
                          </Label>
                          <div style={{ position: "relative" }}>
                            <Input
                              id="confirmPassword"
                              name="confirmPassword"
                              className="form-control"
                              placeholder="Confirm password"
                              type={showPassword ? "text" : "password"}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              value={validation.values.confirmPassword || ""}
                              invalid={
                                validation.touched.confirmPassword &&
                                validation.errors.confirmPassword
                              }
                            />
                            <button
                              type="button"
                              className="position-absolute top-50 translate-middle-y pe-3 border-0 bg-transparent "
                              onClick={togglePasswordVisibility}
                              style={{
                                right: validation.touched.confirmPassword &&
                            validation.errors.confirmPassword  ? "20px" : "0",
                                cursor: "pointer",
                              }}
                            >
                              <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                            </button>
                          </div>
                          {validation.touched.confirmPassword &&
                            validation.errors.confirmPassword ? (
                            <FormFeedback type="invalid">
                              <div>{validation.errors.confirmPassword}</div>
                            </FormFeedback>
                          ) : null}
                        </div>

                        <div className="mt-4">
                          <button
                            className="btn btn-success w-100"
                            type="submit"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                  aria-hidden="true"
                                ></span>
                                Resetting...
                              </>
                            ) : (
                              "Reset Password"
                            )}
                          </button>
                        </div>

                        <div className="mt-4 text-center">
                          <p className="mb-0">
                            Remembered your password?{" "}
                            <Link
                              to="/login"
                              className="fw-semibold text-primary text-decoration-underline"
                            >
                              Sign In
                            </Link>
                          </p>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
      <ToastContainer />
    </React.Fragment>
  );
};

export default ResetPassword;